import type { ColumnSort } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  formatPageNumberToUrlParams,
  parsePageNumberFromUrlParams,
} from './parsers/page';
import {
  formatSortByToUrlParams,
  parseSortByFromUrlParams,
} from './parsers/sortBy';
import type { ArtistUrlParamsHook } from './types';
import { areUrlParamsEqual } from './utils/areUrlParamsEqual';

/**
 * Hook to manage URL parameters.
 * {@link ArtistUrlParamsHook}
 */
export function useUrlParams<T>(
  formatFiltersToUrlParams: (filters: Partial<T>) => URLSearchParams,
  parseFiltersFromUrlParams: (urlParams: URLSearchParams) => T,
  defaultSortBy?: ColumnSort,
): ArtistUrlParamsHook<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    return parseFiltersFromUrlParams(searchParams);
  }, [searchParams, parseFiltersFromUrlParams]);

  const pageNumber = useMemo(() => {
    return parsePageNumberFromUrlParams(searchParams);
  }, [searchParams]);

  const sortBy = useMemo(() => {
    return parseSortByFromUrlParams(searchParams, defaultSortBy);
  }, [searchParams, defaultSortBy]);

  const setPageNumber = useCallback(
    (newPageNumber: number) => {
      setSearchParams(formatPageNumberToUrlParams(newPageNumber, searchParams));
    },
    [searchParams, setSearchParams],
  );

  const setSortBy = useCallback(
    (newSortBy: ColumnSort) => {
      setSearchParams(
        formatSortByToUrlParams(newSortBy, searchParams, defaultSortBy),
      );
    },
    [setSearchParams, searchParams, defaultSortBy],
  );

  const setFilters = useCallback(
    (newFilters: Partial<T>) => {
      const newFilterParams = formatFiltersToUrlParams(newFilters);
      const existingFilterUrlParams = formatFiltersToUrlParams(filters);

      if (areUrlParamsEqual(newFilterParams, existingFilterUrlParams)) {
        return;
      }

      setSearchParams(newFilterParams);
    },
    [filters, setSearchParams, formatFiltersToUrlParams],
  );

  const updateFilters = useCallback(
    (filtersToUpdate: Partial<T>) => {
      const mergedFilters = { ...filters, ...filtersToUpdate };

      setFilters(mergedFilters);
    },
    [filters, setFilters],
  );

  return {
    filters,
    setFilters,
    updateFilters,
    pageNumber,
    setPageNumber,
    sortBy,
    setSortBy,
  };
}
